import { useNavigate } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { DataLoader } from '@/components/ui/DataLoader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useFundReportDefinitions,
  useFundReports,
} from '@/hooks/react-query/useFundReports';
import { formatDate } from '@/utils/formatters';
import type {
  FundReportListItem,
  FundReportStatus,
  ReportDefinition,
} from '@/types/fundReport';
import { ReportSuggestions } from './ReportSuggestions';

const STATUS_LABEL: Record<FundReportStatus, string> = {
  DRAFT: 'Draft',
  APPROVED: 'Approved',
  PUBLISHED: 'Published',
};

const STATUS_VARIANT: Record<
  FundReportStatus,
  'outline' | 'secondary' | 'default'
> = {
  DRAFT: 'outline',
  APPROVED: 'secondary',
  PUBLISHED: 'default',
};

export function FundReportsPanel({ fundId }: { fundId: string }) {
  const navigate = useNavigate();
  const reportsQuery = useFundReports(fundId);
  const { data: definitions = [] } = useFundReportDefinitions(fundId);

  const nameOf = (definitionId: string) =>
    definitions.find(
      (definition: ReportDefinition) => definition.id === definitionId
    )?.name ?? definitionId;

  return (
    <div className="space-y-4">
      <ReportSuggestions fundId={fundId} />

      <DataLoader query={reportsQuery}>
        {(reports: FundReportListItem[]) =>
          reports.length === 0 ? (
            <p className="rounded-md border border-border bg-card p-6 text-center text-sm text-muted-foreground">
              No reports yet.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Report</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Published</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((report) => (
                  <TableRow
                    key={report.id}
                    className="cursor-pointer"
                    onClick={() =>
                      navigate(`/app/funds/${fundId}/reports/${report.id}`)
                    }
                  >
                    <TableCell>{nameOf(report.report_definition_id)}</TableCell>
                    <TableCell>{report.period_label}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[report.status]}>
                        {STATUS_LABEL[report.status]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {report.published_at
                        ? formatDate(report.published_at)
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        }
      </DataLoader>
    </div>
  );
}
